import {
  Children,
  isValidElement,
  useContext,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ArrowRight, CircleAlert } from 'lucide-react';
import { Button, Tabs, TabsList, useIsMobile } from '@shigo-ui/ui';
import { cn } from '@shigo-ui/ui/cn';
import { FormTabsContext } from './dashboardPageFormTabsContext';
import type { DashboardPageFormTabProps } from './DashboardPageFormTab';

export interface DashboardPageFormTabsProps {
  children: ReactNode;
  defaultValue?: string | undefined;
  value?: string | undefined;
  onValueChange?: ((value: string) => void) | undefined;
  layout?: 'sidebar' | 'horizontal' | undefined;
}

export function DashboardPageFormTabs({
  children,
  defaultValue,
  value,
  onValueChange,
  layout,
}: DashboardPageFormTabsProps) {
  const parent = useContext(FormTabsContext);
  const [localValue, setLocalValue] = useState(defaultValue ?? '');
  const selected = value ?? localValue;
  const sidebar = (layout ?? (parent.indicatorId ? 'horizontal' : 'sidebar')) === 'sidebar';
  const isMobile = useIsMobile();
  const indicatorId = useId();
  function change(next: string) {
    if (value === undefined) setLocalValue(next);
    onValueChange?.(next);
  }
  return (
    <FormTabsContext.Provider value={{ sidebar, value: selected, change, indicatorId }}>
      <Tabs
        value={selected}
        onValueChange={change}
        orientation={sidebar && !isMobile ? 'vertical' : 'horizontal'}
        activationMode={sidebar ? 'manual' : 'automatic'}
        // The section rail and the panel fill the form's height down to the action bar.
        className={cn('min-w-0', sidebar && 'grid md:flex-1 md:grid-cols-[13rem_minmax(0,1fr)]')}
      >
        {children}
      </Tabs>
    </FormTabsContext.Provider>
  );
}

export interface DashboardPageFormTabListProps {
  children: ReactNode;
  align?: 'center' | 'start' | undefined;
  navigationLabel?: string | undefined;
  reviewLabel?: string | undefined;
  errorLabel?: ((count: number) => string) | undefined;
}

export function DashboardPageFormTabList({
  children,
  align = 'center',
  navigationLabel = 'Configuration',
  reviewLabel = 'Review issues',
  errorLabel = (count) => `${String(count)} validation errors`,
}: DashboardPageFormTabListProps) {
  const context = useContext(FormTabsContext);
  const listRef = useRef<HTMLDivElement>(null);
  const sections = Children.toArray(children)
    .filter(isValidElement<DashboardPageFormTabProps>)
    .map((child) => child.props);
  const errors = sections.filter((section) => (section.errorCount ?? 0) > 0);
  const count = errors.reduce((sum, section) => sum + (section.errorCount ?? 0), 0);
  const list = (
    <TabsList
      ref={listRef}
      aria-label={navigationLabel}
      className={cn(
        context.sidebar
          ? 'grid h-auto w-full grid-cols-2 justify-start gap-1 rounded-none bg-transparent p-0 sm:grid-cols-3 md:flex md:flex-col md:items-stretch'
          : 'border-border flex h-auto w-full flex-wrap gap-x-4 gap-y-2 rounded-none border-b bg-transparent px-3 py-3 sm:gap-6 sm:p-4',
        !context.sidebar && (align === 'start' ? 'justify-start' : 'justify-center'),
      )}
    >
      {children}
    </TabsList>
  );
  if (!context.sidebar) return list;
  function review() {
    const current = sections.findIndex((section) => section.value === context.value);
    const next = errors.find((section) => sections.indexOf(section) > current) ?? errors[0];
    if (!next) return;
    context.change(next.value);
    Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [])
      .find((tab) => tab.dataset['section'] === next.value)
      ?.focus();
  }
  return (
    <aside className="border-border bg-muted/30 min-w-0 border-b md:col-start-1 md:row-start-1 md:border-r md:border-b-0">
      <div className="space-y-4 p-3 md:sticky md:top-0 md:p-4">
        <p className="text-muted-foreground hidden px-2 text-xs font-medium md:block">
          {navigationLabel}
        </p>
        {list}
        {count > 0 ? (
          <div className="border-border space-y-2 border-t pt-3">
            <p role="status" className="text-destructive flex items-center gap-2 px-2 text-xs">
              <CircleAlert className="size-3.5" aria-hidden="true" />
              {errorLabel(count)}
            </p>
            <Button
              type="button"
              variant="outline"
              className="h-8 w-full justify-between text-xs"
              onClick={review}
            >
              {reviewLabel}
              <ArrowRight className="size-3.5" aria-hidden="true" />
            </Button>
          </div>
        ) : null}
      </div>
    </aside>
  );
}
